import { ListItemAvatar, ListItemIcon, ListItemText } from '@mui/material';

import { usePathname } from 'next/navigation';

import { tss } from 'tss-react/mui';

import type { StaticImport } from 'next/dist/shared/lib/get-img-props';

import { or2ImageLoader } from '@/modules/image/loaders/or2ImageLoader';

import { useTrackIntercomEvent } from '@/hooks/useTrackIntercomEvent';

import { Or2Image } from '../image/Or2Image';

import { Or2Link } from '../navigation/Or2Link';

import { GroupOrServiceAvatar } from '../Avatar/GroupOrServiceAvatar';

import { Or2Avatar } from '../Avatar/Or2Avatar';

import { Or2ListItemButton } from '../data-display/Or2ListItemButton';

import { Icon } from './Icon';

export type Shortcut = StateShortcut | UrlShortcut;

interface ShortcutBase {
  _id?: string | null;
  name: string;
  className?: string;
}

interface UrlShortcut extends ShortcutBase {
  url: string;
  iconUrl: string | StaticImport;
}

interface StateShortcut extends ShortcutBase {
  _id?: string | null;
  path: string;
  basePath: string;
  icon: string;
  iconUrl?: string | null;
  params?: Record<string, string>;
  groupOrServiceName?: string;
}

interface Props {
  shortcut: Shortcut;
  onClick?: () => void;
}

interface StateShortcutIconProps extends Omit<Props, 'shortcut' | 'mobile'> {
  shortcut: StateShortcut;
}

interface UrlShortcutIconProps extends Omit<Props, 'shortcut' | 'mobile'> {
  shortcut: UrlShortcut;
}

const useStyles = tss.create(({ theme }) => {
  return {
    avatar: {
      width: '32px',
      height: '32px',
    },
    listItem: {
      backgroundColor: 'white',
      marginTop: theme.spacing(1),
    },
  };
});

const StateShortcutIcon = (props: StateShortcutIconProps) => {
  const { classes } = useStyles();
  const pathname = usePathname();

  const { shortcut, onClick } = props;

  const { name, basePath, path, params, icon, iconUrl } = shortcut;
  const isActive = pathname.startsWith(basePath);

  const hasParams = params && Object.keys(params).length > 0;

  return (
    <Or2ListItemButton
      data-test={`${basePath}-shortcut`}
      className={classes.listItem}
      href={`${path}${hasParams ? `?${new URLSearchParams(params)}` : ''}`}
      selected={isActive}
      LinkComponent={Or2Link}
      onClick={onClick}
    >
      <ListItemAvatar>
        {shortcut.groupOrServiceName ? (
          <GroupOrServiceAvatar
            groupOrServiceName={shortcut.groupOrServiceName}
            iconUrl={iconUrl}
            size={32}
            className={classes.avatar}
          />
        ) : (
          <Or2Avatar className={classes.avatar}>
            {iconUrl ? (
              <Or2Image
                src={iconUrl}
                alt={`${name} icon`}
                width={32}
                height={32}
                loader={or2ImageLoader}
              />
            ) : (
              <Icon>{icon}</Icon>
            )}
          </Or2Avatar>
        )}
      </ListItemAvatar>
      <ListItemText primary={name} />
    </Or2ListItemButton>
  );
};

const UrlShortcutIcon = (props: UrlShortcutIconProps) => {
  const { classes } = useStyles();
  const { shortcut, ...other } = props;
  const { name, iconUrl, _id, url } = shortcut;

  const trackIntercomEvent = useTrackIntercomEvent();

  return (
    <Or2ListItemButton
      {...other}
      data-test={`${_id}-shortcut`}
      className={classes.listItem}
      href={url}
      LinkComponent={Or2Link}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackIntercomEvent(`Click ${name} (sidebar)`, { url })}
    >
      <ListItemIcon>
        <ListItemAvatar>
          <Or2Avatar className={classes.avatar}>
            <Or2Image
              src={iconUrl}
              alt={`${name} icon`}
              width={32}
              height={32}
              loader={or2ImageLoader}
            />
          </Or2Avatar>
        </ListItemAvatar>
      </ListItemIcon>
      <ListItemText primary={name} />
    </Or2ListItemButton>
  );
};

export const ShortcutIcon = ({ shortcut, ...other }: Props) => {
  return 'basePath' in shortcut ? (
    <StateShortcutIcon shortcut={shortcut} {...other} />
  ) : (
    <UrlShortcutIcon shortcut={shortcut} {...other} />
  );
};
